import { useCacheRevalidation } from "@prismicio/editor-support/Suspense"
import { useEffect, useState } from "react"

import type { EditorConfig } from "../../../EditorConfig"
import {
  type MediaAssetType,
  searchMedia,
  searchMediaArgs
} from "./mediaLibraryData"

interface UseMediaLibraryCacheInvalidationArgs {
  revalidate: () => void
  config: EditorConfig
  assetType: MediaAssetType
}

export function useMediaLibraryCacheInvalidation(
  args: UseMediaLibraryCacheInvalidationArgs
) {
  const { revalidate, config, assetType } = args
  const {
    assetsApiBaseUrl = undefined,
    repository = "",
    authStrategy = "token"
  } = config.mediaLibrary ? config : {}

  const [isMediaRevalidationInProgress, setIsMediaRevalidationInProgress] =
    useState(false)

  const [isMediaCacheInvalid, setIsMediaCacheInvalid] = useState(false)

  useCacheRevalidation({
    fetcher: searchMedia,
    requestParams: searchMediaArgs({
      assetsApiBaseUrl,
      repository,
      authStrategy,
      assetType,
      keyword: "",
      useOpenSearch: false,
      page: 0
    }),
    validator: (response, cache) => {
      const responseFirstId = response.items[0]?.id ?? ""
      const mediaFirstId = cache[0]?.items[0]?.id ?? ""

      return (
        // check if the first media id in the cache is matching the first id on the server
        responseFirstId !== mediaFirstId ||
        // check if the total amount of items in the cache and on the server are equal
        response.total !== cache[0]?.total
      )
    },
    onCacheInvalidated: () => setIsMediaCacheInvalid(true),
    every: 60_000
  })

  // cleanup states to reset any effects and UI based on their state
  useEffect(() => {
    if (isMediaRevalidationInProgress) {
      setIsMediaCacheInvalid(false)
      setIsMediaRevalidationInProgress(false)
    }
  }, [isMediaRevalidationInProgress])

  function revalidateMedia() {
    setIsMediaRevalidationInProgress(true)
    revalidate()
  }
  return { isMediaRevalidationInProgress, isMediaCacheInvalid, revalidateMedia }
}
